<template>
  <div>
    <Navigation
      v-bind:Breadcrumb="[
        { name: 'Home', route: 'Home' },
        { name: 'Staff', route: 'Admin' },
        { name: 'View Organisations', route: 'Admin_Organisations' },
        { name: 'Manage Organisations', route: 'Admin_Organisations' },
      ]"
    />
    <div class="placeholder">
      <div class="row">
        <div class="col-6">
          <div class="error" v-for="error in errors" :key="error">
            {{ error }}
          </div>
          <div class="success" v-for="message in messages" :key="message">
            {{ message }}
          </div>
          <form class="form" v-on:submit.prevent="UpdateOrganisation">
            <label for="form.name">Organisation Name</label>
            <input type="text" class="input" v-model="form.name" />
            <label for="form.address">Address</label>
            <textarea
              class="input left"
              v-model="form.address"
              rows="4"
              required
            ></textarea>
            <label for="form.postcode">Postcode</label>
            <input 
              type="text" 
              class="input" 
              v-model="form.postcode" 
              pattern="[A-Za-z0-9\s-]*"
              title="Please enter a valid postcode (letters, numbers, spaces, and - only)"
              maxlength="10"
              @input="validatePostcodeInput"
              required 
            />
            <label for="form.contact_email">Contact Email</label>
            <input
              type="email"
              class="input"
              v-model="form.contact_email"
              required
            />
            <label for="form.contact_telephone">Contact Telephone</label>
            <input
              type="tel"
              class="input"
              v-model="form.contact_telephone"
              pattern="[0-9+() -]*"
              title="Please enter a valid phone number (numbers, +, (, ), - only)"
              @input="validatePhoneInput"
              required
            />
            <br />
            <br />
            
            <!-- ORGANISATION ACCESS LEVEL -->
            <h3 class="access-level">Data Access Level</h3>
            <p class="access-description">
              Choose the level of business data that organisation users will be able to view
              <span class="tooltip-container">
                <span class="help-icon">?</span>
                <span class="tooltip">
                  <strong>Detailed View:</strong> Users can see individual business-level data and detailed information<br><br>
                  <strong>Aggregated View:</strong> Users can only see high-level aggregated data across all businesses
                </span>
              </span>
            </p>
            
            <!-- Detailed View -->
            <label class="radio_label">
              <input
                type="radio"
                name="access_level"
                value="detailed"
                v-model="selectedAccessLevel"
              />
              Detailed View
            </label>
            
            <!-- Aggregated View -->
            <label class="radio_label">
              <input
                type="radio"
                name="access_level"
                value="aggregated"
                v-model="selectedAccessLevel"
              />
              Aggregated View
            </label>
            <br />
            <br />
            
            <input type="submit" class="btn btn-green" value="Save Changes" />
          </form>
        </div>
        <div class="col-6">
          <table class="table">
            <thead>
              <tr>
                <td>Name</td>
                <td>Email</td>
                <td colspan="2">Role</td>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td colspan="1">
                  <label for="adduser.email">Email Address</label>
                  <input
                    id="adduser.email"
                    type="email"
                    class="input"
                    v-model="adduser.email"
                    placeholder="example@example.com"
                  />
                </td>
                <td>
                  <label for="adduser.role">Role</label>
                  <select
                    class="input"
                    v-model="adduser.role"
                    id="adduser.role"
                  >
                    <option value="1">Sales Rep</option>
                    <option value="2">Member</option>
                    <option value="3">Manager</option>
                  </select>
                </td>
                <td colspan="2">
                  <button
                    class="btn btn-green"
                    v-on:click="addUserToOrganisation"
                  >
                    Invite User <i class="icon icon-pen"></i>
                  </button>
                </td>
              </tr>
              <tr v-for="(user, index) in users" :key="user">
                <td>{{ user.name }}</td>
                <td>{{ user.email }}</td>
                <td>{{ roleTypes(user.role) }}</td>
                <td>
                  <div
                    class="btn btn-orange"
                    v-on:click="RemoveUserFromOrganisation(user.user_id, index)"
                  >
                    <span class="icon icon-delete"></span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  
    <!-- Confirmation Modal for Detailed Access -->
    <div
      id="confirm-detailed-access-modal"
      class="modal"
      v-if="showConfirmDetailed"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-detailed-access-title"
      @click.self="cancelDetailedAccess"
    >
      <div class="modal-content">
        <span class="close" @click="cancelDetailedAccess" aria-label="Close">&times;</span>        
        <div class="confirm-access-copy">
          <p class="confirm-lead">
            You are about to grant <strong>Detailed View</strong> access to this organisation.
          </p>
          <div class="confirm-detail">
            <span class="detail-badge">Detailed View</span>
            <span class="detail-text">Users can see individual business-level data and detailed information.</span>
          </div>
          <p class="confirm-warning">
            This is a sensitive permission. Only proceed if you are certain the organisation should view identifiable business data. If unsure, choose <em>Aggregated View</em> instead.
          </p>
        </div>
        <div style="margin-top:20px; display:flex; gap:10px; flex-wrap:wrap;">
          <button type="button" class="btn btn-green" @click="confirmDetailedAccess">
            Yes, Grant Detailed Access
          </button>
          <button type="button" class="btn btn-red" @click="cancelDetailedAccess">
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useStore } from "vuex";
import Navigation from "@/components/Navigation.vue";

export default {
  setup() {
    const store = useStore();
    return { store };
  },
  data() {
    return {
      messages: "",
      errors: "",
      form: {},
      users: [],
      adduser: { email: "", role: 1 },
      selectedAccessLevel: "aggregated",
      originalAccessLevel: "aggregated",
      showConfirmDetailed: false,
      detailedAccessConfirmed: false,
    };
  },
  components: { Navigation },
  watch: {
    selectedAccessLevel(newLevel) {
      this.form.access_level = newLevel === "detailed" ? 1 : 0;
      if (newLevel === "detailed" && this.originalAccessLevel !== "detailed") {
        this.detailedAccessConfirmed = false;
      }
    },
  },
  methods: {
    GetOrganisation() {
      this.store
        .dispatch("Admin/GetOrganisationInfo", this.$route.params.id)
        .then((result) => {
          this.form = result.data;
          this.users = result.users;
          this.selectedAccessLevel = result.data.access_level === 1 ? 'detailed' : 'aggregated';
          this.originalAccessLevel = this.selectedAccessLevel;
          if (this.originalAccessLevel === "detailed") {
            this.detailedAccessConfirmed = true;
          }
        })
        .catch((err) => {
          this.errors = err.messages;
          this.messages = "";
        });
    },
    UpdateOrganisation() {
      this.messages = "";
      this.errors = "";
      this.form.id = this.$route.params.id;
      const switchingToDetailed =
        this.selectedAccessLevel === "detailed" && this.originalAccessLevel !== "detailed";
      if (switchingToDetailed && !this.detailedAccessConfirmed) {
        this.showConfirmDetailed = true;
        this.$nextTick(() => {
          const btn = this.$el.querySelector(
            "#confirm-detailed-access-modal .btn.btn-green"
          );
          if (btn) btn.focus();
        });
        return;
      }
      this.performUpdate();
    },
    performUpdate() {
      this.store
        .dispatch("Admin/UpdateOrganisationInfo", this.form)
        .then((result) => {
          this.messages = result.messages;
          this.errors = "";
          this.originalAccessLevel = this.selectedAccessLevel;
        })
        .catch((err) => {
          this.errors = err.messages;
          this.messages = "";
        });
    },
    confirmDetailedAccess() {
      this.detailedAccessConfirmed = true;
      this.showConfirmDetailed = false;
      this.performUpdate();
    },
    cancelDetailedAccess() {
      this.showConfirmDetailed = false;
    },
    validatePhoneInput(event) {
      // Allow only numbers, +, (, ), space, and -
      const allowedPattern = /[^0-9+() -]/g;
      const cleanValue = event.target.value.replace(allowedPattern, '');
      if (event.target.value !== cleanValue) {
        this.form.contact_telephone = cleanValue;
      }
    },
    validatePostcodeInput(event) {
      // Allow only letters, numbers, spaces, and -
      const allowedPattern = /[^A-Za-z0-9\s-]/g;
      const cleanValue = event.target.value.replace(allowedPattern, '');
      if (event.target.value !== cleanValue) {
        this.form.postcode = cleanValue;
      }
    },
    onEsc(e) {
      if (e.key === "Escape" && this.showConfirmDetailed) {
        this.cancelDetailedAccess();
      }
    },
    roleTypes(int) {
      switch (int) {
        case 1:
          return "Sales Rep";
        case 2:
          return "Member";
        case 3:
          return "Manager";
        default:
          return "Please Select a Role";
      }
    },
    addUserToOrganisation() {
      if (this.adduser.email !== "" && this.adduser.role !== "") {
        this.store
          .dispatch("Admin/AddUserToOrganisation", {
            id: this.$route.params.id,
            email: this.adduser.email,
            role: this.adduser.role,
          })
          .then((result) => {
            this.messages = result.messages;
            this.errors = "";
          })
          .catch((err) => {
            this.errors = err.messages;
            this.messages = "";
          });
      }
    },
    RemoveUserFromOrganisation(user, index) {
      this.store
        .dispatch("Admin/RemoveUserFromOrganisation", {
          id: this.$route.params.id,
          user_id: user,
        })
        .then((result) => {
          this.messages = result.messages;
          this.errors = "";
          this.users.splice(index, 1);
        })
        .catch((err) => {
          this.errors = err.messages;
          this.messages = "";
        });
    },
  },
  mounted() {
    this.GetOrganisation();
    window.addEventListener("keydown", this.onEsc);
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this.onEsc);
  },
};
</script>

<style>
.access-level {
  margin-top: 5px;
  margin-bottom: 5px;
  color: #35bba4;
}

.access-description {
  font-size: 14px;
  color: #666;
  margin-bottom: 10px;
  font-style: italic;
}

.access-level + p + label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
}

.access-level + p + label input[type="radio"] {
  margin-right: 8px;
}

.radio_label{
  font-size: 14px !important;
}

.tooltip-container {
  position: relative;
  display: inline-block;
  margin-left: 5px;
}

.help-icon {
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: #e0e7ff;
  color: #667085;
  text-align: center;
  font-size: 12px;
  font-weight: bold;
  line-height: 16px;
  cursor: help;
  user-select: none;
}

.tooltip {
  visibility: hidden;
  width: 280px;
  background-color: #333;
  color: #fff;
  text-align: left;
  border-radius: 6px;
  padding: 12px;
  position: absolute;
  z-index: 1;
  bottom: 125%;
  left: 50%;
  margin-left: -140px;
  opacity: 0;
  transition: opacity 0.3s;
  font-size: 12px;
  line-height: 1.4;
}

.tooltip::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 50%;
  margin-left: -5px;
  border-width: 5px;
  border-style: solid;
  border-color: #333 transparent transparent transparent;
}

.tooltip-container:hover .tooltip {
  visibility: visible;
  opacity: 1;
}


.btn.btn-red {
  background: #e34343;
  border: 1px solid #c52f2f;
  color: #fff;
}
.btn.btn-red:hover {
  background: #c52f2f;
}

.modal {
  position: fixed;
  z-index: 99999;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  overflow: auto;
  background-color: rgba(0,0,0,0.4);
}
.modal-content {
  background-color: #fefefe;
  margin: 100px auto;
  padding: 20px;
  border: 1px solid #888;
  width: 50%;
  max-width: 640px;
  border-radius: 10px;
}
.close {
  color: #aaaaaa;
  float: right;
  font-size: 28px;
  font-weight: bold;
  cursor: pointer;
}
.close:hover, .close:focus { color:#000; }

.confirm-access-copy { font-size:14px; line-height:1.5; }
.confirm-lead { 
  margin:0 0 12px 0; 
  font-weight:600; 
  color:#04a394;
}
.confirm-detail {
  display:flex; 
  align-items:flex-start; 
  gap:8px; 
  background:#f3fcfb;
  border:1px solid #c9efe9; 
  padding:10px 12px; 
  border-radius:8px; 
  margin-bottom:14px;
}
.detail-badge { 
  background:#04a394; 
  color:#fff; 
  padding:2px 8px; 
  border-radius:12px; 
  font-size:11px; 
  text-transform:uppercase; 
  letter-spacing:.5px; 
  line-height:18px; 
  font-weight:600; 
}
.detail-text { flex:1; font-size:13px; color:#2f4a47; }
.confirm-warning {   
  padding:10px 12px; 
  border-radius:8px; 
  color:#b53434; 
  font-size:13px; 
  margin:0 0 4px 0; 
}
</style>